import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format, differenceInDays } from 'date-fns';
import axios from 'axios';
import { 
  Search, Calendar, MapPin, Shield, Clock, Award, 
  ChevronRight, Users, Fuel, Settings2, Plane, CreditCard,
  CheckCircle, HelpCircle, Star, ChevronDown
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { cn } from '../lib/utils';

import GoogleReviews from '../components/GoogleReviews';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Search form state - Date Range
  const [category, setCategory] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [returnLocation, setReturnLocation] = useState('');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, catRes, vehRes] = await Promise.all([
          axios.get(`${API_URL}/api/locations`),
          axios.get(`${API_URL}/api/vehicles/categories/list`),
          axios.get(`${API_URL}/api/vehicles?available=true`)
        ]);
        setLocations(locRes.data);
        setCategories(catRes.data);
        setFeaturedVehicles(vehRes.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleDateSelect = (range) => {
    setDateRange(range || { from: null, to: null });
    // Close calendar after selecting return date
    if (range?.from && range?.to) {
      setTimeout(() => setCalendarOpen(false), 300);
    }
  };

  const rentalDays = dateRange.from && dateRange.to 
    ? differenceInDays(dateRange.to, dateRange.from) 
    : 0;

  const handleSearch = () => {
    if (!dateRange.from || !dateRange.to) return;
    
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (pickupLocation) params.append('pickupLocation', pickupLocation);
    if (returnLocation) params.append('returnLocation', returnLocation);
    params.append('pickupDate', format(dateRange.from, 'yyyy-MM-dd'));
    params.append('returnDate', format(dateRange.to, 'yyyy-MM-dd'));
    
    navigate(`/vehicles?${params.toString()}`);
  };

  const getLocalizedName = (item) => {
    return i18n.language === 'fr' ? item.name_fr : item.name_en;
  };

  const trustBadges = [
    { icon: Shield, key: 'noHiddenFees' },
    { icon: Plane, key: 'airportDelivery' },
    { icon: CreditCard, key: 'depositOnly' },
  ];

  const features = [
    { icon: Shield, titleKey: 'insuredVehicles', descKey: 'insuredVehiclesDesc' },
    { icon: Plane, titleKey: 'airportDelivery', descKey: 'airportDeliveryDesc' },
    { icon: CreditCard, titleKey: 'noHiddenFees', descKey: 'noHiddenFeesDesc' },
    { icon: Clock, titleKey: 'support247', descKey: 'support247Desc' },
  ];

  const faqItems = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10', 'q11', 'q12'];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent z-10" />
          <img
            src="https://images.unsplash.com/photo-1449965408869-ebd3fee56f03?w=1920&q=80"
            alt="Geneva Lake View"
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className="section-container relative z-20 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <div className="space-y-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[#4A945D] font-semibold">
                {t('hero.subtitle')}
              </p>
              <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-none text-[#181B1F]">
                {t('hero.title')}
              </h1>
              <p className="text-lg text-gray-600 max-w-lg leading-relaxed">
                {t('hero.description')}
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 pt-4">
                {trustBadges.map((badge, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 bg-[#4A945D]/10 border border-[#4A945D]/20 rounded-full"
                  >
                    <CheckCircle className="w-4 h-4 text-[#4A945D]" strokeWidth={2} />
                    <span className="text-gray-700 text-sm font-medium">{t(`trust.${badge.key}`)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Search Card */}
            <div
              className="bg-white shadow-2xl border border-gray-200 rounded-xl p-8 space-y-6"
              data-testid="search-form"
            >
              <h3 className="font-serif text-2xl text-[#181B1F] font-light">
                {t('hero.searchTitle')}
              </h3>

              {/* Category Select */}
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-gray-500">
                  {t('search.category')}
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="input-default" data-testid="category-select">
                    <SelectValue placeholder={t('search.allCategories')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-lg">
                    <SelectItem value="all" className="text-gray-700">
                      {t('search.allCategories')}
                    </SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="text-gray-700">
                        {getLocalizedName(cat)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location Selects */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    {t('search.pickupLocation')}
                  </label>
                  <Select value={pickupLocation} onValueChange={setPickupLocation}>
                    <SelectTrigger className="input-default" data-testid="pickup-location-select">
                      <SelectValue placeholder={t('search.pickupLocation')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 shadow-lg">
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-gray-700">
                          {getLocalizedName(loc)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    {t('search.returnLocation')}
                  </label>
                  <Select value={returnLocation} onValueChange={setReturnLocation}>
                    <SelectTrigger className="input-default" data-testid="return-location-select">
                      <SelectValue placeholder={t('search.returnLocation')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 shadow-lg">
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-gray-700">
                          {getLocalizedName(loc)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date Range Picker */}
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-white/50">
                  {t('search.selectDateRange')}
                </label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left input-default border h-14",
                        !dateRange.from && "text-gray-400"
                      )}
                      data-testid="date-range-btn"
                    >
                      <Calendar className="mr-3 h-5 w-5 text-primary" />
                      <div className="flex-1">
                        {dateRange.from ? (
                          dateRange.to ? (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700">
                                {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                              </span>
                              {rentalDays > 0 && (
                                <span className="text-primary font-medium">
                                  {rentalDays} {t('search.nights')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-700">{format(dateRange.from, "PPP")} - Select return</span>
                          )
                        ) : (
                          <span className="text-gray-400">{t('search.selectDates')}</span>
                        )}
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200 shadow-xl" align="center">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={handleDateSelect}
                      numberOfMonths={2}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Search Button */}
              <Button 
                onClick={handleSearch}
                disabled={!dateRange.from || !dateRange.to}
                className="w-full btn-primary h-14 text-base"
                data-testid="search-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                {t('search.searchBtn')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Features Section */}
      <section className="section-spacing bg-[#F9FAFB]" data-testid="features-section">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-feature group"
              >
                <feature.icon 
                  className="w-8 h-8 text-[#4A945D] mb-4 group-hover:scale-110 transition-transform duration-300" 
                  strokeWidth={1.5} 
                />
                <h3 className="font-serif text-xl text-[#181B1F] mb-2">{t(`trust.${feature.titleKey}`)}</h3>
                <p className="text-gray-500 text-sm">{t(`trust.${feature.descKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Fleet Section */}
      <section className="section-spacing bg-white" data-testid="fleet-section">
        <div className="section-container">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#4A945D] font-semibold mb-2">
              {i18n.language === 'fr' ? 'Notre Flotte' : 'Our Fleet'}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-[#181B1F]">
              {i18n.language === 'fr' ? 'Véhicules Populaires' : 'Popular Vehicles'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredVehicles.map((vehicle, index) => (
              <div
                key={vehicle.id}
                className="card-vehicle group cursor-pointer"
                onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                data-testid={`vehicle-card-${vehicle.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={vehicle.images[0] || 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600'}
                    alt={vehicle.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-[#4A945D] font-semibold">
                      {i18n.language === 'fr' ? vehicle.category_name_fr : vehicle.category_name_en}
                    </span>
                    <span className="text-xs text-gray-400">{vehicle.year}</span>
                  </div>
                  <h3 className="font-serif text-xl text-[#181B1F]">{vehicle.name}</h3>
                  <div className="flex items-center gap-4 text-gray-400 text-sm">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" strokeWidth={1.5} />
                      {vehicle.seats}
                    </span>
                    <span className="flex items-center gap-1">
                      <Settings2 className="w-4 h-4" strokeWidth={1.5} />
                      {vehicle.transmission === 'automatic' ? 'Auto' : 'Manual'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Fuel className="w-4 h-4" strokeWidth={1.5} />
                      {vehicle.fuel_type}
                    </span>
                  </div>
                  <div className="flex items-end justify-between pt-2 border-t border-gray-100">
                    <div>
                      <span className="text-gray-400 text-xs">{t('vehicle.from')}</span>
                      <p className="text-[#181B1F] font-medium">
                        CHF {vehicle.base_daily_rate}
                        <span className="text-gray-400 text-sm">{t('vehicle.perDay')}</span>
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#4A945D] group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cancellation Policy Section */}
      <section className="py-16 bg-[#4A945D]/5 border-y border-[#4A945D]/10" data-testid="cancellation-section">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#4A945D]/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#4A945D]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#181B1F]">{t('cancellation.title')}</h3>
                <p className="text-gray-600 text-sm">{t('cancellation.free')}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#4A945D]" />
                <span className="text-gray-600">{t('cancellation.free')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing bg-[#F9FAFB]" data-testid="faq-section">
        <div className="section-container max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#4A945D] font-semibold mb-2">
              FAQ
            </p>
            <h2 className="font-serif text-4xl text-[#181B1F] font-light">
              {t('faq.title')}
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem 
                key={item} 
                value={item}
                className="border border-gray-200 rounded-xl bg-white px-6 shadow-sm"
              >
                <AccordionTrigger className="text-[#181B1F] hover:text-[#181B1F] hover:no-underline py-5">
                  <span className="text-left font-medium">{t(`faq.${item}`)}</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 whitespace-pre-line">
                  {t(`faq.a${index + 1}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          {/* Vehicle-specific note */}
          <div className="mt-8 p-4 bg-[#4A945D]/10 border border-[#4A945D]/20 rounded-xl">
            <p className="text-gray-600 text-sm">
              {t('faq.vehicleNote')}
            </p>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-12 bg-white" data-testid="reviews-section">
        <div className="section-container">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#4A945D] font-semibold mb-2">
              {i18n.language === 'fr' ? 'Témoignages' : 'Testimonials'}
            </p>
            <h2 className="font-serif text-3xl text-[#181B1F] font-light">
              {i18n.language === 'fr' ? 'Ce que nos clients disent' : 'What Our Customers Say'}
            </h2>
          </div>
          <GoogleReviews variant="homepage" maxReviews={4} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing relative overflow-hidden bg-[#0A0F0D]" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#4A945D]/20 to-transparent" />
        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-6">
                {i18n.language === 'fr' 
                  ? 'Besoin d\'aide pour choisir?' 
                  : 'Need Help Choosing?'}
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                {i18n.language === 'fr'
                  ? 'Notre équipe est disponible pour vous conseiller et répondre à toutes vos questions.'
                  : 'Our team is available to advise you and answer all your questions.'}
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/fleet-rental')}
                  className="btn-primary"
                >
                  {t('nav.fleetRental')}
                </Button>
                <Button 
                  onClick={() => navigate('/long-term')}
                  variant="outline"
                  className="h-12 px-8 border-2 border-white/30 hover:bg-white/10 bg-transparent text-white font-semibold tracking-wide text-sm transition-all duration-300 rounded-full"
                >
                  {t('nav.longTerm')}
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1617814076367-b759c7d7e738?w=800"
                alt="Premium SUV"
                className="w-full rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
